import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import './Chat.css';

function Chat() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [autoScroll, setAutoScroll] = useState(true);
  const [fontSize, setFontSize] = useState(16);
  const [enabledPlatforms, setEnabledPlatforms] = useState({
    'twitch-main': true,
    'twitch-247': false,
    'youtube': true,
    'discord': false
  });
  const [viewerCounts, setViewerCounts] = useState({
    twitchMain: 0,
    youtube: 0
  });
  const [connected, setConnected] = useState(false);
  const [maxMessages] = useState(100);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const channelEmoteMapRef = useRef(new Map());

  // Load platform settings from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('chatPlatformSettings');
    if (saved) {
      setEnabledPlatforms(JSON.parse(saved));
    }
  }, []);

  // Save platform settings to localStorage
  const togglePlatform = (platform) => {
    const newSettings = {
      ...enabledPlatforms,
      [platform]: !enabledPlatforms[platform]
    };
    setEnabledPlatforms(newSettings);
    localStorage.setItem('chatPlatformSettings', JSON.stringify(newSettings));
  };

  // Load channel emotes (7TV, BTTV, Twitch subscriber emotes) once on mount
  useEffect(() => {
    loadChannelEmotes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadChannelEmotes = async () => {
    try {
      // Get channel settings to find the twitch-main channel ID
      const settingsRes = await axios.get(`${BACKEND_URL}/api/settings`).catch(() => null);
      const twitchSettings = settingsRes?.data?.find?.(s => s.platform === 'twitch-main');
      if (!twitchSettings) return;

      const config = typeof twitchSettings.config === 'string'
        ? JSON.parse(twitchSettings.config)
        : twitchSettings.config;

      const channelId = config?.channelId;
      const clientId = config?.clientId;

      const emoteMap = new Map();

      // Load 7TV channel emotes
      if (channelId) {
        try {
          const res7tv = await fetch(`https://7tv.io/v3/users/twitch/${channelId}`);
          if (res7tv.ok) {
            const data = await res7tv.json();
            const emotes = data?.emote_set?.emotes || [];
            emotes.forEach(e => {
              const url = e.data?.host?.url
                ? `https:${e.data.host.url}/1x.webp`
                : null;
              if (url) emoteMap.set(e.name, url);
            });
          }
        } catch (_) { /* 7TV not reachable – continue without */ }

        // Load BTTV channel emotes
        try {
          const resBttv = await fetch(`https://api.betterttv.net/3/cached/users/twitch/${channelId}`);
          if (resBttv.ok) {
            const data = await resBttv.json();
            const emotes = [
              ...(data?.channelEmotes || []),
              ...(data?.sharedEmotes || [])
            ];
            emotes.forEach(e => {
              emoteMap.set(e.code, `https://cdn.betterttv.net/emote/${e.id}/1x`);
            });
          }
        } catch (_) { /* BTTV not reachable – continue without */ }

        // Load Twitch subscriber emotes (requires clientId)
        if (clientId) {
          try {
            const oauthToken = config?.oauth?.startsWith('oauth:')
              ? config.oauth.substring(6)
              : config?.oauth;
            if (oauthToken) {
              const resTwitch = await fetch(
                `https://api.twitch.tv/helix/chat/emotes?broadcaster_id=${channelId}`,
                { headers: { 'Client-ID': clientId, 'Authorization': `Bearer ${oauthToken}` } }
              );
              if (resTwitch.ok) {
                const data = await resTwitch.json();
                (data?.data || []).forEach(e => {
                  const url = e.images?.url_1x || null;
                  if (url) emoteMap.set(e.name, url);
                });
              }
            }
          } catch (_) { /* Twitch API not reachable – continue without */ }
        }
      }

      channelEmoteMapRef.current = emoteMap;
    } catch (_) { /* Settings not reachable – chat works without emotes */ }
  };

  // Render a message string with emote images
  // twitchEmoteTags: object from tmi.js tags.emotes, e.g. { "25": "0-4,6-10" }
  // channelEmoteMap: Map<name, url> for 7TV/BTTV/subscriber emotes
  const renderMessageWithEmotes = useCallback((message, twitchEmoteTags, channelEmoteMap) => {
    if (!message) return null;

    // Build a sorted list of Twitch emote replacements by position
    const replacements = []; // { start, end, id }

    if (twitchEmoteTags && typeof twitchEmoteTags === 'object') {
      Object.entries(twitchEmoteTags).forEach(([emoteId, positions]) => {
        const posStr = Array.isArray(positions) ? positions.join(',') : String(positions);
        posStr.split(',').forEach(range => {
          const [start, end] = range.split('-').map(Number);
          if (!isNaN(start) && !isNaN(end)) {
            replacements.push({ start, end, emoteId, type: 'twitch' });
          }
        });
      });
    }

    // Sort by start position
    replacements.sort((a, b) => a.start - b.start);

    const elements = [];
    let cursor = 0;

    // Convert message to array of Unicode code points to handle multi-byte chars correctly
    const chars = [...message];

    const addTextSpan = (text, key) => {
      if (!text) return;
      // Word-by-word matching for 7TV/BTTV emotes
      const words = text.split(' ');
      words.forEach((word, i) => {
        if (channelEmoteMap && channelEmoteMap.has(word)) {
          const url = channelEmoteMap.get(word);
          elements.push(
            <img
              key={`${key}-w${i}`}
              src={url}
              alt={word}
              title={word}
              style={{ height: '28px', verticalAlign: 'middle', margin: '0 1px' }}
              onError={(ev) => {
                ev.target.style.display = 'none';
                const span = document.createElement('span');
                span.textContent = word;
                ev.target.parentNode?.insertBefore(span, ev.target);
              }}
            />
          );
        } else {
          const spacer = i < words.length - 1 ? ' ' : '';
          elements.push(<span key={`${key}-w${i}`}>{word}{spacer}</span>);
        }
        if (i < words.length - 1 && !channelEmoteMap?.has(word)) {
          // space already added above
        }
      });
    };

    replacements.forEach((rep, idx) => {
      // Text before this emote
      if (rep.start > cursor) {
        const textBefore = chars.slice(cursor, rep.start).join('');
        addTextSpan(textBefore, `pre-${idx}`);
      }

      const emoteText = chars.slice(rep.start, rep.end + 1).join('');
      const src = `https://static-cdn.jtvnw.net/emoticons/v2/${rep.emoteId}/default/dark/1.0`;

      elements.push(
        <img
          key={`emote-${idx}`}
          src={src}
          alt={emoteText}
          title={emoteText}
          style={{ height: '28px', verticalAlign: 'middle', margin: '0 1px' }}
          onError={(ev) => {
            ev.target.style.display = 'none';
            const span = document.createElement('span');
            span.textContent = emoteText;
            ev.target.parentNode?.insertBefore(span, ev.target);
          }}
        />
      );

      cursor = rep.end + 1;
    });

    // Remaining text after last Twitch emote
    if (cursor < chars.length) {
      const remaining = chars.slice(cursor).join('');
      addTextSpan(remaining, 'tail');
    }

    // If no Twitch emotes were found, still do word-matching for 7TV/BTTV
    if (replacements.length === 0) {
      elements.length = 0;
      addTextSpan(message, 'full');
    }

    return elements.length > 0 ? elements : message;
  }, []);

  useEffect(() => {
    connectSocket();
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoScroll) {
      scrollToBottom();
    }
  }, [messages, autoScroll]);

  const connectSocket = () => {
    socketRef.current = io(BACKEND_URL);

    socketRef.current.on('connect', () => {
      setConnected(true);
      console.log('Chat connected');
    });

    socketRef.current.on('disconnect', () => {
      setConnected(false);
      console.log('Chat disconnected');
    });

    // Listen for viewer count updates
    socketRef.current.on('viewer:count', (data) => {
      setViewerCounts(prev => ({
        ...prev,
        [data.platform]: data.count
      }));
    });

    socketRef.current.on('chat:message', (data) => {
      setMessages(prev => {
        const newMessages = [...prev, {
          id: data.id || Date.now() + Math.random(),
          messageId: data.messageId,
          platform: data.platform,
          username: data.username,
          userId: data.userId,
          message: data.message,
          emotes: data.emotes || null,
          timestamp: new Date(),
          badges: data.badges || {},
          color: data.color || getRandomColor(),
          deleted: false
        }];
        
        // Limit messages for performance
        if (newMessages.length > maxMessages) {
          return newMessages.slice(-maxMessages);
        }
        return newMessages;
      });
    });

    socketRef.current.on('chat:messageDeleted', (data) => {
      setMessages(prev => prev.map(msg => {
        // Delete by messageId
        if (data.messageId && msg.messageId === data.messageId) {
          return { ...msg, deleted: true };
        }
        // Delete by userId
        if (data.userId && msg.userId === data.userId) {
          return { ...msg, deleted: true };
        }
        // Delete by username
        if (data.username && msg.username === data.username) {
          return { ...msg, deleted: true };
        }
        return msg;
      }));
    });

    socketRef.current.on('chat:viewers', (data) => {
      setViewerCounts(data);
    });

    // Alert Events
    const alertEvents = [
      'twitch-follow',
      'twitch-subscription',
      'twitch-gift-sub',
      'twitch-cheer',
      'twitch-raid',
      'twitch-host',
      'youtube-superchat',
      'youtube-subscription',
      'youtube-membership'
    ];

    alertEvents.forEach(event => {
      socketRef.current.on(event, (data) => {
        showAlert(event, data);
      });
    });
  };

  const reconnect = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    setMessages([]);
    connectSocket();
  };

  const showAlert = (type, data) => {
    const alertId = Date.now();
    const alertConfig = getAlertConfig(type, data);
    
    setAlerts(prev => [...prev, { id: alertId, ...alertConfig }]);

    // Remove alert after 6 seconds
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    }, 6000);
  };

  const getAlertConfig = (type, data) => {
    const configs = {
      'twitch-follow': {
        platform: 'twitch',
        icon: '💜',
        title: 'Neuer Follower!',
        message: `${data.username} folgt jetzt!`,
        className: 'follow'
      },
      'twitch-subscription': {
        platform: 'twitch',
        icon: '⭐',
        title: data.isResub ? 'Resub!' : 'Neuer Subscriber!',
        message: data.isResub 
          ? `${data.username} ist seit ${data.months} Monaten dabei!`
          : `${data.username} ist jetzt Subscriber!`,
        subtext: `Tier ${data.tier}`,
        className: 'subscription'
      },
      'twitch-gift-sub': {
        platform: 'twitch',
        icon: '🎁',
        title: 'Sub Geschenk!',
        message: `${data.gifter} hat ${data.recipient} ein Sub geschenkt!`,
        subtext: `Tier ${data.tier}`,
        className: 'gift-sub'
      },
      'twitch-cheer': {
        platform: 'twitch',
        icon: '💎',
        title: 'Bits!',
        message: `${data.username} hat ${data.bits} Bits gespendet!`,
        subtext: data.message,
        className: 'cheer'
      },
      'twitch-raid': {
        platform: 'twitch',
        icon: '🚀',
        title: 'Raid!',
        message: `${data.username} raided mit ${data.viewers} Zuschauern!`,
        className: 'raid'
      },
      'twitch-host': {
        platform: 'twitch',
        icon: '📺',
        title: 'Host!',
        message: `${data.username} hostet mit ${data.viewers} Zuschauern!`,
        className: 'host'
      },
      'youtube-superchat': {
        platform: 'youtube',
        icon: '💰',
        title: 'Super Chat!',
        message: `${data.username}: ${data.amount}`,
        subtext: data.message,
        className: 'superchat'
      },
      'youtube-subscription': {
        platform: 'youtube',
        icon: '🎉',
        title: 'Neuer Subscriber!',
        message: `${data.username} hat abonniert!`,
        className: 'subscription'
      },
      'youtube-membership': {
        platform: 'youtube',
        icon: '👑',
        title: 'Neues Mitglied!',
        message: `${data.username} ist jetzt Mitglied!`,
        subtext: data.level,
        className: 'membership'
      }
    };

    return configs[type] || {
      platform: 'system',
      icon: '📢',
      title: 'Event',
      message: JSON.stringify(data),
      className: 'default'
    };
  };

  const deleteMessage = (msg) => {
    if (!socketRef.current) return;
    
    socketRef.current.emit('chat:deleteMessage', {
      platform: msg.platform,
      messageId: msg.messageId,
      userId: msg.userId
    });
  };

  const timeoutUser = (msg, duration) => {
    if (!socketRef.current) return;
    
    socketRef.current.emit('chat:timeoutUser', {
      platform: msg.platform,
      username: msg.username,
      userId: msg.userId,
      duration
    });
  };

  const showModActions = (msg, event) => {
    event.preventDefault();
    
    const actions = [
      { label: 'Nachricht löschen', action: () => deleteMessage(msg) },
      { label: 'Timeout 60s', action: () => timeoutUser(msg, 60) },
      { label: 'Timeout 10min', action: () => timeoutUser(msg, 600) },
      { label: 'Timeout 1h', action: () => timeoutUser(msg, 3600) }
    ];
    
    // Simple confirm dialog for now
    const choice = window.confirm('Nachricht löschen?\n\nOK = Löschen\nAbbrechen = Timeout-Optionen');
    
    if (choice) {
      deleteMessage(msg);
    } else {
      const timeoutChoice = window.prompt('Timeout Dauer:\n1 = 60 Sekunden\n2 = 10 Minuten\n3 = 1 Stunde', '1');
      if (timeoutChoice === '1') timeoutUser(msg, 60);
      else if (timeoutChoice === '2') timeoutUser(msg, 600);
      else if (timeoutChoice === '3') timeoutUser(msg, 3600);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const increaseFontSize = () => {
    setFontSize(prev => Math.min(prev + 2, 32));
  };

  const decreaseFontSize = () => {
    setFontSize(prev => Math.max(prev - 2, 12));
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'twitch-main': return '💜';
      case 'youtube': return '▶️';
      default: return '💬';
    }
  };

  const getRandomColor = () => {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57',
                    '#ff9ff3', '#54a0ff', '#5f27cd', '#00d2d3', '#ff9f43'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const renderBadges = (badges) => {
    const badgeElements = [];
    
    if (badges.broadcaster) {
      badgeElements.push(<span key="broadcaster" className="badge broadcaster" title="Broadcaster">📡</span>);
    }
    if (badges.moderator || badges.mod) {
      badgeElements.push(<span key="mod" className="badge mod" title="Moderator">🛡️</span>);
    }
    if (badges.subscriber || badges.sub) {
      badgeElements.push(<span key="sub" className="badge sub" title="Subscriber">⭐</span>);
    }
    if (badges.vip) {
      badgeElements.push(<span key="vip" className="badge vip" title="VIP">💎</span>);
    }
    
    return badgeElements;
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className="chat-page-fullscreen">
      {/* Alert Overlay Container */}
      <div className="alert-overlay-container">
        {alerts.map(alert => (
          <div key={alert.id} className={`alert-overlay ${alert.className} ${alert.platform}`}>
            <div className="alert-icon">{alert.icon}</div>
            <div className="alert-content">
              <div className="alert-title">{alert.title}</div>
              <div className="alert-message">{alert.message}</div>
              {alert.subtext && <div className="alert-subtext">{alert.subtext}</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="chat-header">
        <button className="back-btn" onClick={() => navigate('/')} title="Zurück">
          ← Zurück
        </button>
        
        <div className="platform-filters">
          <button 
            className={`platform-filter-btn ${enabledPlatforms['twitch-main'] ? 'active' : ''}`}
            onClick={() => togglePlatform('twitch-main')}
            title="Twitch Main"
          >
            💜 Main
          </button>
          <button 
            className={`platform-filter-btn ${enabledPlatforms['twitch-247'] ? 'active' : ''}`}
            onClick={() => togglePlatform('twitch-247')}
            title="Twitch 24/7"
          >
            🔄 24/7
          </button>
          <button 
            className={`platform-filter-btn ${enabledPlatforms['youtube'] ? 'active' : ''}`}
            onClick={() => togglePlatform('youtube')}
            title="YouTube"
          >
            ▶️ YouTube
          </button>
          <button 
            className={`platform-filter-btn ${enabledPlatforms['discord'] ? 'active' : ''}`}
            onClick={() => togglePlatform('discord')}
            title="Discord"
          >
            💙 Discord
          </button>
        </div>
        
        <div className="viewer-stats">
          <div className="viewer-stat twitch">
            <span className="platform-icon">💜</span>
            <span className="viewer-count">{viewerCounts.twitchMain}</span>
          </div>
          <div className="viewer-stat youtube">
            <span className="platform-icon">▶️</span>
            <span className="viewer-count">{viewerCounts.youtube}</span>
          </div>
        </div>
        
        <div className="chat-controls">
          <button 
            className={`control-btn ${connected ? 'connected' : 'disconnected'}`}
            onClick={reconnect}
            title="Reconnect"
          >
            🔄
          </button>
          <button 
            className={`control-btn ${autoScroll ? 'active' : ''}`}
            onClick={() => setAutoScroll(!autoScroll)}
            title="Auto Scroll"
          >
            📜
          </button>
        </div>
      </div>

      <div className="chat-messages" style={{ fontSize: `${fontSize}px` }}>
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>Warte auf Chat-Nachrichten...</p>
            <p className="hint">Starte die Services im Dashboard um Chat-Nachrichten zu empfangen</p>
          </div>
        ) : (
          messages
            .filter(msg => enabledPlatforms[msg.platform])
            .map(msg => (
            <div key={msg.id} className={`chat-message ${msg.deleted ? 'deleted' : ''}`}>
              <span className="platform-icon">{getPlatformIcon(msg.platform)}</span>
              <button 
                className="mod-action-btn"
                onClick={(e) => showModActions(msg, e)}
                title="Moderation"
              >
                🗑️
              </button>
              <div className="message-content">
                <span className="message-inline">
                  {renderBadges(msg.badges)}
                  <span className="message-username" style={{ color: msg.color }}>
                    {msg.username}:
                  </span>
                  <span className="message-text">
                    {renderMessageWithEmotes(msg.message, msg.emotes, channelEmoteMapRef.current)}
                  </span>
                  {msg.deleted && <span className="deleted-label"> [Gelöscht]</span>}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-footer">
        <button className="size-btn" onClick={decreaseFontSize} title="Schrift verkleinern">
          A-
        </button>
        <span className="font-size-display">{fontSize}px</span>
        <button className="size-btn" onClick={increaseFontSize} title="Schrift vergrößern">
          A+
        </button>
      </div>
    </div>
  );
}

export default Chat;
